import * as React from 'react';

import { useEnrollSupplier, useSendInvitation } from '@/api/use-suppliers';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// =============================================================================
// supplier-enroll-screen.tsx — Écran A-05 « Enrôlement fournisseur » (Admin).
// =============================================================================
// Enrôlement en 2 ACTES DISTINCTS (decisions.md, GATE_0 D6) — JAMAIS un seul
// bouton :
//   ACTE 1 — « Créer le fournisseur » : POST :siret/enroll → établissement
//            Brouillon + MASTER « Invité », AUCUN event. Le SIRET est la
//            ressource d'URL (saisi ici, transmis à l'adaptateur routeur).
//   ACTE 2 — « Envoyer l'invitation » (bouton SÉPARÉ, activé seulement APRÈS
//            l'acte 1) : POST :siret/invitation → Brouillon→Soumis + émission de
//            DE.SupplierEnrolled.
//
// L'autorité (validité, dérogation domaine, émission d'event) reste l'API. L'UI
// expose 2 actions distinctes et reflète l'état (créé / invité).
//
// WCAG 2.1 AA : formulaire avec <label> liés, messages de statut role="status" /
// role="alert", navigation clavier native.

export interface SupplierEnrollScreenProps {
  /** Quitte l'écran d'enrôlement (retour liste). Porté par l'adaptateur routeur. */
  readonly onDone: () => void;
  /** Ouvre la fiche A-04 du SIRET enrôlé. Porté par l'adaptateur routeur. */
  readonly onOpenDetail: (siret: string) => void;
}

export function SupplierEnrollScreen({
  onDone,
  onOpenDetail,
}: SupplierEnrollScreenProps): React.JSX.Element {
  const enroll = useEnrollSupplier();
  const sendInvitation = useSendInvitation();

  const [siret, setSiret] = React.useState('');
  const [masterEmail, setMasterEmail] = React.useState('');
  const [masterNom, setMasterNom] = React.useState('');
  const [masterPrenom, setMasterPrenom] = React.useState('');
  /** SIRET effectivement créé (acte 1 réussi) — débloque l'acte 2. */
  const [createdSiret, setCreatedSiret] = React.useState<string | null>(null);
  const [invitationSent, setInvitationSent] = React.useState(false);
  /**
   * Dérogation domaine responsabilisée (§2.2) : l'admin force l'enrôlement alors
   * que l'e-mail du maître n'appartient pas au domaine attendu du SIRET. Transmis
   * à l'ACTE 2 (`domainOverride`, contrat InvitationInput). L'AUTORITÉ (validité,
   * traçabilité de la dérogation) reste l'API : l'UI relaie l'intention assumée.
   */
  const [domainOverride, setDomainOverride] = React.useState(false);

  const onCreate = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      // Normalisation SIRET : ne conserve que les chiffres (les espaces/séparateurs de
      // saisie provoquaient un 500 côté API — désormais un SIRET propre part au serveur,
      // l'autorité de validité (^\d{14}$) restant l'API).
      const normalizedSiret = siret.replace(/\D/gu, '');
      enroll.mutate(
        {
          siret: normalizedSiret,
          body: { masterEmail, masterNom, masterPrenom },
        },
        { onSuccess: () => setCreatedSiret(normalizedSiret) },
      );
    },
    [enroll, siret, masterEmail, masterNom, masterPrenom],
  );

  const onSendInvitation = React.useCallback((): void => {
    if (createdSiret === null) {
      return;
    }
    sendInvitation.mutate(
      {
        siret: createdSiret,
        // Le champ n'est transmis que si la dérogation est explicitement assumée
        // (sinon corps vide : comportement par défaut, domaine vérifié côté API).
        body: domainOverride ? { domainOverride: true } : {},
      },
      { onSuccess: () => setInvitationSent(true) },
    );
  }, [sendInvitation, createdSiret, domainOverride]);

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Enrôler un fournisseur
          </h1>
          <p className="text-muted-foreground">
            Deux étapes distinctes : créer l'établissement, puis envoyer
            l'invitation au maître.
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Retour à la liste
        </Button>
      </header>

      {/* ACTE 1 — Créer le fournisseur (établissement Brouillon, AUCUN event). */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Étape 1 — Créer le fournisseur
          </CardTitle>
          <CardDescription>
            Crée l'établissement en brouillon avec son contact maître. Aucune
            notification n'est envoyée à cette étape.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="enroll-siret">SIRET (14 chiffres)</Label>
              <Input
                id="enroll-siret"
                name="siret"
                inputMode="numeric"
                value={siret}
                onChange={(e) => setSiret(e.target.value)}
                disabled={createdSiret !== null}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enroll-master-email">
                E-mail du maître (MASTER)
              </Label>
              <Input
                id="enroll-master-email"
                name="masterEmail"
                type="email"
                value={masterEmail}
                onChange={(e) => setMasterEmail(e.target.value)}
                disabled={createdSiret !== null}
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="enroll-master-nom">Nom du maître (MASTER)</Label>
                <Input
                  id="enroll-master-nom"
                  name="masterNom"
                  autoComplete="family-name"
                  value={masterNom}
                  onChange={(e) => setMasterNom(e.target.value)}
                  disabled={createdSiret !== null}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="enroll-master-prenom">
                  Prénom du maître (MASTER)
                </Label>
                <Input
                  id="enroll-master-prenom"
                  name="masterPrenom"
                  autoComplete="given-name"
                  value={masterPrenom}
                  onChange={(e) => setMasterPrenom(e.target.value)}
                  disabled={createdSiret !== null}
                  required
                />
              </div>
            </div>

            {enroll.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {enroll.error.message}
              </p>
            ) : null}

            {createdSiret !== null ? (
              <p role="status" className="text-sm text-foreground">
                Fournisseur créé en brouillon (maître invité). Passez à l'étape 2.
              </p>
            ) : null}

            <Button
              type="submit"
              disabled={enroll.isPending || createdSiret !== null}
            >
              {enroll.isPending ? 'Création…' : 'Créer le fournisseur'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* ACTE 2 — Envoyer l'invitation (SÉPARÉ, débloqué après l'acte 1). */}
      <Card aria-disabled={createdSiret === null}>
        <CardHeader>
          <CardTitle className="text-base">
            Étape 2 — Envoyer l'invitation
          </CardTitle>
          <CardDescription>
            Passe l'établissement en « Soumis » et notifie le maître. Action
            distincte de la création.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {createdSiret === null ? (
            <p className="text-sm text-muted-foreground">
              Créez d'abord le fournisseur (étape 1) pour débloquer l'envoi de
              l'invitation.
            </p>
          ) : null}

          {sendInvitation.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {sendInvitation.error.message}
            </p>
          ) : null}

          {invitationSent ? (
            <p role="status" className="text-sm text-foreground">
              Invitation envoyée. L'établissement est passé en « Soumis ».
            </p>
          ) : null}

          {/* §2.2 — Dérogation domaine responsabilisée (case « forcer hors-domaine »). */}
          <div className="flex items-start gap-3">
            <input
              id="enroll-domain-override"
              name="domainOverride"
              type="checkbox"
              checked={domainOverride}
              onChange={(e) => setDomainOverride(e.target.checked)}
              disabled={createdSiret === null || invitationSent}
              aria-describedby="enroll-domain-override-help"
              className="mt-1 h-4 w-4 shrink-0 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div className="space-y-1">
              <Label htmlFor="enroll-domain-override">
                Forcer l'enrôlement hors-domaine
              </Label>
              <p
                id="enroll-domain-override-help"
                className="text-sm text-muted-foreground"
              >
                À cocher en connaissance de cause : l'adresse du maître
                n'appartient pas au domaine attendu du SIRET. La dérogation est
                tracée et relève de la responsabilité de l'administrateur.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              onClick={onSendInvitation}
              disabled={
                createdSiret === null ||
                sendInvitation.isPending ||
                invitationSent
              }
            >
              {sendInvitation.isPending ? 'Envoi…' : "Envoyer l'invitation"}
            </Button>
            {createdSiret !== null ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenDetail(createdSiret)}
              >
                Ouvrir la fiche
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
